import { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUsageStore } from '../../stores/usageStore';

/**
 * Scene clips, and whether the AI Agent is a studio (components/AIAgent/StudioShell.jsx:
 * its modes in a rail, scenes a place of their own at pages/SceneStudioPage.jsx)
 * instead of tabs with a scene mode inside the video composer.
 *
 * `scenesOn`: the API makes scenes (`/usage/metrics` → scenes.enabled).
 * `studioOn`: the studio is shown. For everyone once the API says `scenes.studio`
 * (its SCENE_STUDIO switch: no frontend deploy turns it on or off); for admins and a
 * browser with `cs_scene_studio=1` before that.
 *
 * The answer arrives with the usage, a moment after the page. Until it does, the
 * last answer this browser got is used, and the studio where there is none: it is
 * what production shows, and drawing the old page first made every visit open on
 * tabs and a mode switch that then jumped to the rail.
 *
 * Scenes and narrated videos are separate modes (the owner's call, 2026-09-30): a
 * video built from scenes was tried for an hour and taken out, to be thought through
 * properly later. The API's `scenes.in_videos` is not read.
 */
const KEPT = 'cs_scenes';
const ASSUMED = { enabled: true, studio: true, seconds: 8 };

function kept() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(KEPT) || 'null');
    return stored && typeof stored === 'object' ? stored : ASSUMED;
  } catch {
    return ASSUMED;
  }
}

export function sceneStudioFrom(scenes, user) {
  const scenesOn = Boolean(scenes?.enabled);
  let asked = false;
  try { asked = window.localStorage.getItem('cs_scene_studio') === '1'; } catch { /* no storage: not asked */ }
  return {
    scenesOn,
    studioOn: scenesOn && Boolean(scenes?.studio || user?.is_admin || asked),
    seconds: scenes?.seconds || 8,
  };
}

export default function useSceneStudio() {
  // No provider (a page mounted on its own): nobody is signed in
  const user = useAuth()?.user;
  const metrics = useUsageStore((state) => state.metrics);
  const answered = Boolean(metrics);
  const scenes = answered ? metrics.scenes : kept();
  const enabled = Boolean(metrics?.scenes?.enabled);
  const studio = Boolean(metrics?.scenes?.studio);
  const seconds = metrics?.scenes?.seconds || 8;
  useEffect(() => {
    if (!answered) return;
    try { window.localStorage.setItem(KEPT, JSON.stringify({ enabled, studio, seconds })); } catch { /* asked again next time */ }
  }, [answered, enabled, studio, seconds]);
  return sceneStudioFrom(scenes, user);
}
